import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Flame, Loader2 } from "lucide-react";

interface I18n { fr: string; en?: string }
interface Category {
  id: string;
  position: number;
  name_i18n: I18n;
  emoji: string | null;
}
interface Item {
  id: string;
  category_id: string;
  position: number;
  name_i18n: I18n;
  description_i18n: I18n;
  price: number | null;
  currency: string;
  emoji: string | null;
  photo_url: string | null;
  dietary: string[];
  allergens: string[];
  spice_level: number;
}

const DIET_LABEL: Record<string, string> = {
  vegetarien: "Végé",
  vegan: "Vegan",
  sans_gluten: "Sans gluten",
  halal: "Halal",
  casher: "Casher",
};

const formatPrice = (price: number | null, currency: string) => {
  if (price == null) return "";
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format(price);
  } catch {
    return `${price.toFixed(2)} ${currency}`;
  }
};

export const NativeMenuRenderer = ({ clientId }: { clientId: string }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const [{ data: cats }, { data: its }] = await Promise.all([
        (supabase as any).from("menu_categories").select("*").eq("client_id", clientId).eq("enabled", true).order("position"),
        (supabase as any).from("menu_items").select("*").eq("client_id", clientId).eq("enabled", true).eq("is_draft", false).order("position"),
      ]);
      if (cancel) return;
      setCategories((cats ?? []) as Category[]);
      setItems((its ?? []) as Item[]);
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [clientId]);

  if (loading) {
    return <div className="flex items-center justify-center py-8 text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Chargement du menu…</div>;
  }

  if (!categories.length) {
    return <p className="text-sm text-muted-foreground text-center py-6">Aucun plat publié pour le moment.</p>;
  }

  const hasAnyPhoto = items.some((i) => i.photo_url);

  return (
    <div className="space-y-6">
      {categories.map((cat) => {
        const catItems = items.filter((i) => i.category_id === cat.id);
        if (!catItems.length) return null;
        return (
          <section key={cat.id} className="space-y-3">
            <h3 className="text-lg font-semibold flex items-center gap-2 border-b border-border/40 pb-1">
              {cat.emoji && <span>{cat.emoji}</span>}
              <span>{cat.name_i18n?.fr || cat.name_i18n?.en}</span>
            </h3>

            {hasAnyPhoto ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {catItems.map((it) => (
                  <article key={it.id} className="rounded-xl border border-border/40 bg-card/60 overflow-hidden">
                    {it.photo_url && (
                      <img src={it.photo_url} alt={it.name_i18n?.fr ?? ""} className="w-full h-32 object-cover" loading="lazy" />
                    )}
                    <div className="p-3 space-y-1">
                      <div className="flex justify-between items-start gap-2">
                        <h4 className="font-medium text-sm">
                          {it.emoji && <span className="mr-1">{it.emoji}</span>}
                          {it.name_i18n?.fr || it.name_i18n?.en}
                        </h4>
                        {it.price != null && (
                          <span className="text-sm font-semibold whitespace-nowrap">{formatPrice(it.price, it.currency)}</span>
                        )}
                      </div>
                      {it.description_i18n?.fr && (
                        <p className="text-xs text-muted-foreground">{it.description_i18n.fr}</p>
                      )}
                      <ItemTags item={it} />
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <ul className="space-y-2">
                {catItems.map((it) => (
                  <li key={it.id} className="flex justify-between gap-3 items-start py-1.5 border-b border-dashed border-border/30">
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {it.emoji && <span className="mr-1">{it.emoji}</span>}
                        {it.name_i18n?.fr || it.name_i18n?.en}
                      </p>
                      {it.description_i18n?.fr && (
                        <p className="text-xs text-muted-foreground">{it.description_i18n.fr}</p>
                      )}
                      <ItemTags item={it} />
                    </div>
                    {it.price != null && (
                      <span className="text-sm font-semibold whitespace-nowrap">{formatPrice(it.price, it.currency)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
};

const ItemTags = ({ item }: { item: Item }) => {
  if (!item.dietary.length && !item.allergens.length && !item.spice_level) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {item.dietary.map((d) => (
        <Badge key={d} variant="secondary" className="text-[10px] py-0">{DIET_LABEL[d] ?? d}</Badge>
      ))}
      {Array.from({ length: item.spice_level }).map((_, i) => (
        <Flame key={i} className="w-3 h-3 text-orange-500" />
      ))}
      {item.allergens.length > 0 && (
        <span className="text-[10px] text-muted-foreground">
          Allergènes : {item.allergens.join(", ")}
        </span>
      )}
    </div>
  );
};

export default NativeMenuRenderer;
